import { useCallback, useMemo } from "react";
import { Button, Stack } from "@mui/material";
import { useForm } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import { FormValues } from "@/types/widget";
import { LineGraphConfiguration } from "@/features/admin/components/line-graph-configuration/LineGraphConfiguration";
import ThresholdsConfiguration from "@/features/admin/components/thresholds-configuration/ThresholdsConfiguration";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import {
  LineGraphEdrLine,
  LineGraphEdrLineAllOfColorEnum,
  LineGraphEdrLineAllOfLineStyleEnum,
  LineGraphEdrLineAllOfLineWidthEnum,
} from "@/lib/api";

export interface Threshold {
  id?: string;
  title: string;
  value: number;
  threshold_type: string;
  color?: string;
}

const DEFAULT_THRESHOLD_VALUES: Threshold = {
  title: "",
  value: 0,
  threshold_type: "line",
};

const DEFAULT_LINE_VALUES: LineGraphEdrLine = {
  id: "",
  title: "",
  type: "",
  edr_dataset_id: "",
  edr_parameter: "",
  line_style: LineGraphEdrLineAllOfLineStyleEnum.Solid,
  line_width: LineGraphEdrLineAllOfLineWidthEnum.Regular,
  color: LineGraphEdrLineAllOfColorEnum.Blue,
};

const DEFAULT_VALUES: {
  thresholds: Threshold[];
  lines: LineGraphEdrLine[];
} = {
  thresholds: [],
  lines: [DEFAULT_LINE_VALUES],
};

const getInitialValue = (values: FormValues) => {
  const configuration = values?.configuration;
  if (!configuration) return DEFAULT_VALUES;

  const initialValues = DEFAULT_VALUES;

  if ("lines" in configuration) {
    initialValues.lines = configuration.lines as LineGraphEdrLine[];
  }
  if ("thresholds" in configuration) {
    initialValues.thresholds = configuration.thresholds as Threshold[];
  }

  return initialValues;
};

export function LineGraphForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const initialValues = getInitialValue(values);
  const form = useForm();
  const formValues = form.getState().values;
  const lines = formValues?.configuration?.lines ?? [];
  const thresholds = formValues?.configuration?.thresholds;

  const lineCount = useMemo(() => {
    if (!lines) return 1;
    return lines.length;
  }, [lines]);
  const thresholdCount = useMemo(() => {
    if (!thresholds) return 0;
    return thresholds.length;
  }, [thresholds]);

  // remove from array
  const removeLine = useCallback(
    (id: number) => {
      if (lines) {
        const newLines = [...lines];
        newLines.splice(id, 1);
        form.change("configuration.lines", newLines);
      }
    },
    [lines],
  );
  const removeThreshold = useCallback(
    (id: number) => {
      if (thresholds) {
        const newThresholds = [...thresholds];
        newThresholds.splice(id, 1);
        form.change("configuration.thresholds", newThresholds);
      }
    },
    [thresholds],
  );

  // add to array
  const addLine = useCallback(() => {
    form.change(`configuration.lines[${lineCount}]`, DEFAULT_LINE_VALUES);
  }, [lineCount]);
  const addThreshold = useCallback(() => {
    form.change(`configuration.thresholds[${thresholdCount}]`, DEFAULT_THRESHOLD_VALUES);
  }, [thresholdCount]);

  return (
    <>
      <Stack gap={2} mt={2}>
        <Stack border={"1px solid rgba(0,0,0, 0.1)"} padding={2} gap={1}>
          {[...Array(thresholdCount).keys()].map((index) => (
            <ThresholdsConfiguration
              key={index}
              name="configuration.thresholds"
              index={index}
              thresholds={thresholds}
              initialValues={initialValues["thresholds"]}
              defaultValues={DEFAULT_THRESHOLD_VALUES}
              removeThreshold={() => removeThreshold(index)}
            />
          ))}

          <Button onClick={addThreshold}>Nieuwe threshold</Button>
        </Stack>

        {[...Array(lineCount).keys()].map((index) => (
          <LineGraphConfiguration
            key={index}
            index={index}
            name="configuration.lines"
            initialValue={initialValues["lines"][index] || DEFAULT_LINE_VALUES}
            removeLine={() => removeLine(index)}
            lines={lines}
          />
        ))}

        <Button onClick={addLine}>{t("admin.widgets.fields.lineGraph.lines.new")}</Button>
      </Stack>
    </>
  );
}
